import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, ZoomIn, ZoomOut, RotateCw, Loader2 } from 'lucide-react';

/**
 * DocumentPreviewModal - In-page document preview without download option
 * 
 * Use this for agents who should only view documents, not download them.
 * Clients and admins can use the regular window.open approach.
 * 
 * Props:
 * - documentId: The document ID to fetch from backend proxy
 * - fileUrl: Direct URL (fallback if documentId not provided)
 * - fileName: Display name for the document
 * - mimeType: MIME type of the document
 * - isAgentDocument: If true, use agent document endpoint
 * - isJsonbDocument: If true, use view-by-url endpoint for JSONB stored docs
 * - filingId: Required for agent documents and JSONB documents
 */
export function DocumentPreviewModal({ 
  isOpen, 
  onClose, 
  documentId,
  fileUrl, 
  fileName,
  mimeType,
  isAgentDocument = false,
  isJsonbDocument = false,
  filingId = null
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (isOpen && (documentId || fileUrl)) {
      loadDocument();
    }
    
    return () => {
      // Cleanup blob URL when modal closes
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [isOpen, documentId, fileUrl, isAgentDocument, isJsonbDocument, filingId]);

  const loadDocument = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      // Use appropriate endpoint based on document type
      let fetchUrl;
      let fetchOptions = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      if (isAgentDocument && filingId && documentId) {
        // Agent documents stored in JSONB
        fetchUrl = `${backendUrl}/api/files/agent-document/${filingId}/${documentId}`;
      } else if (isJsonbDocument && fileUrl && filingId) {
        // JSONB documents (like otherDocuments) - use POST endpoint
        fetchUrl = `${backendUrl}/api/files/view-by-url`;
        fetchOptions = {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            file_url: fileUrl,
            entity_type: 'tax_filing',
            entity_id: filingId
          })
        };
      } else if (documentId) {
        // Regular documents in documents table - include file_url as fallback
        fetchUrl = `${backendUrl}/api/files/view/${documentId}`;
        if (fileUrl) {
          fetchUrl += `?file_url=${encodeURIComponent(fileUrl)}`;
        }
      } else if (fileUrl) {
        // Fallback to direct URL
        fetchUrl = fileUrl.startsWith('http') ? fileUrl : `${backendUrl}${fileUrl}`;
      } else {
        throw new Error('No document source provided');
      }
      
      const response = await fetch(fetchUrl, fetchOptions);
      
      if (!response.ok) {
        throw new Error('Failed to load document');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
    } catch (err) {
      console.error('Error loading document:', err);
      setError('Failed to load document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 50));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);

  const isPDF = fileName?.toLowerCase().endsWith('.pdf') || mimeType?.includes('pdf');
  const isImage = mimeType?.startsWith('image/') || 
    /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName || '');

  const handleClose = () => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    setZoom(100);
    setRotation(0);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] flex flex-col p-0" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader className="px-4 py-3 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg truncate pr-4">
              {fileName || 'Document Preview'}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {isImage && (
                <>
                  <Button variant="ghost" size="sm" onClick={handleZoomOut} disabled={zoom <= 50}>
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground w-12 text-center">{zoom}%</span>
                  <Button variant="ghost" size="sm" onClick={handleZoomIn} disabled={zoom >= 200}>
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleRotate}>
                    <RotateCw className="h-4 w-4" />
                  </Button>
                </>
              )}
              <Button variant="ghost" size="sm" onClick={handleClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-auto bg-muted/30 flex items-center justify-center">
          {loading && (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading document...</p>
            </div>
          )}
          
          {error && (
            <div className="text-center p-4">
              <p className="text-destructive">{error}</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={loadDocument}>
                Try Again
              </Button>
            </div>
          )}
          
          {!loading && !error && blobUrl && (
            <>
              {isPDF ? (
                <iframe
                  src={`${blobUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                  className="w-full h-full border-0"
                  title={fileName || 'Document Preview'}
                  style={{ 
                    pointerEvents: 'auto',
                  }}
                />
              ) : isImage ? (
                <div 
                  className="p-4 overflow-auto w-full h-full flex items-center justify-center"
                  style={{ 
                    cursor: zoom > 100 ? 'move' : 'default' 
                  }}
                >
                  <img
                    src={blobUrl}
                    alt={fileName || 'Document'}
                    style={{
                      transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                      transition: 'transform 0.2s ease',
                      maxWidth: zoom === 100 ? '100%' : 'none',
                      maxHeight: zoom === 100 ? '100%' : 'none',
                    }}
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                </div>
              ) : (
                <div className="text-center p-4">
                  <p className="text-muted-foreground">
                    Preview not available for this file type.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="px-4 py-2 border-t text-xs text-muted-foreground text-center flex-shrink-0">
          Document preview only. Contact admin if you need a copy of this document.
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DocumentPreviewModal;
